import 'react-app-polyfill/ie11';
import 'react-app-polyfill/stable';
import React from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import xssFilters from 'xss-filters';
import includes from 'lodash/includes';
import {asyncWithLDProvider} from 'launchdarkly-react-client-sdk';
import {getLDEnvironment} from './utils/utilLaunchDarkly';
import {getUserEntitlementLevel} from '@adsk/ha-entitlements-util';
import {v1 as uuidv1} from 'uuid';
import log from 'loglevel';
import App from './components/App';
import ProviderWrapper from './ProviderWrapper';

import './scss/_index.scss';
import {
  siteTierConfig,
  siteConfigFallbacks,
  siteTier,
  enabledLangsAndCountry,
} from './app.config';
import {
  initUhmFrameListeners,
  initAnalytics,
  loadScriptFromUrl,
  memoizedParseQueryParams,
  parseQueryParams,
  buildLdUserObject,
  isStorageAccessible,
  setNeutrontHandler,
  forgeSetupAsync,
  hasChatInPrevPage,
  eteRouter,
  shouldUseAuthFromHostPage
} from './utils';
import {store} from './configureStore';
//import {hasChatInPrevPage} from '@adsk/dh-contact-react-components/InlineChat/util';
import {updateUserInteracted} from './actions/uiActions';
import worker from 'workerize-loader!./workers/worker'; // eslint-disable-line import/no-webpack-loader-syntax
let workerInstance = worker(); // `new` is optional

// map env/siteTier to LaunchDarkly Environment IDs
const LDEnv =
  window.location.hostname === 'localhost.autodesk.com'
    ? 'development'
    : getLDEnvironment(siteTier);

console.log('LD environment', LDEnv);

// chat session function
const {hasCurrentChatSession} = hasChatInPrevPage();

// Reset the userInteracted flag to unless on page reload unless there is a active chat.
if (!hasCurrentChatSession) {
  // Added setTimeout to prevent updating the state before redux-persist restore the state from localstorage.
  setTimeout(() => {
    store.dispatch(updateUserInteracted(false));
  }, 1);
}

const isLocalStorageAvailable = isStorageAccessible(() => localStorage);
const isSessionStorageAvailable = isStorageAccessible(() => sessionStorage);
// get query params
const queryParams = window.location.search;
const getParentUrlParams = memoizedParseQueryParams(queryParams, [
  'uhmParentUrl',
  'uhmParentDomain',
  'uhmParentReferrer',
  'analyticsLogging',
  'allowImproveExperience',
]);

// Global error handler - catch uncaught errors during initialization and report to host page
window.addEventListener('error', (event) => {
  try {
    const params = new URLSearchParams(queryParams);
    const parentDomain = params.get('uhmParentDomain') || '*';
    
    if (event.error && !window.__uhmInitComplete) {
      const errorType = 'UNCAUGHT_ERROR';
      const errorMessage = event.error.message || event.message || 'Uncaught error during initialization';
      
      window.parent.postMessage({
        type: 'UHM_LOAD_ERROR',
        errorType: errorType,
        errorMessage: errorMessage,
        errorDetails: {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
          stack: event.error.stack,
          url: window.location.href || 'unknown'
        }
      }, parentDomain);
    }
  } catch (e) {
    log.error('Failed to report uncaught error to host page:', e);
  }
});
// non-cached parameters to enable switching versions, product context etc.
const getParentUrlParamsNoCache = parseQueryParams(queryParams, [
  'product',
  'release',
  'pageTitle',
  'language',
  'dtVersion',
  'viewSource',
  'showFullPage',
  'containerId',
  'allowFeatureTesting',
  'allowFlag',
  'deepLinkTo',
  'modality',
  'integrationType',
  'isMobileDevice',
  'code',
  'state',
  'redirectOnly',
  'serviceappointid',
  'Day',
  'customerstarttime',
  'customertimezone',
  'sacCancelLang',
  'agentType',
  'agentEmail',
  'sacProduct',
  'customerphone',
  'serviceappointid',
  'productCode',
  'topic',
  'langCode',
  'sacReschLang',
  'EvntSubject',
  'showContextualPopUp',
]);

// consuming page url
let parentUrl;
if (getParentUrlParams.uhmParentUrl) {
  parentUrl = xssFilters.uriQueryInUnQuotedAttr(
    decodeURIComponent(getParentUrlParams.uhmParentUrl)
  );
} else {
  parentUrl = `${window.location.protocol}//${window.location.host}${window.location.pathname}`;
}
parentUrl = parentUrl.replace(/\/$/, '');

// consuming page domain for postMessage target origin
let parentDomain;
if (getParentUrlParams.uhmParentDomain) {
  parentDomain = xssFilters.uriQueryInUnQuotedAttr(
    decodeURIComponent(getParentUrlParams.uhmParentDomain)
  );
} else {
  parentDomain = `${window.location.protocol}//${window.location.host}`;
}
parentDomain = parentDomain.replace(/\/$/, '');

// referer
let parentReferrer = '';
if (getParentUrlParams.uhmParentReferrer) {
  parentReferrer = xssFilters.uriQueryInUnQuotedAttr(
    decodeURIComponent(getParentUrlParams.uhmParentReferrer)
  );
}

/*
 * GDPR flags from consuming site
 */
//1st adskGdprAnalytics
let allowAnalyticsLogging = false;
if (getParentUrlParams.analyticsLogging) {
  allowAnalyticsLogging = xssFilters.uriQueryInUnQuotedAttr(
    decodeURIComponent(getParentUrlParams.analyticsLogging)
  );
}
//2nd adskGdprImproveExp
let allowImproveExperience = false;
if (getParentUrlParams.allowImproveExperience) {
  allowImproveExperience = xssFilters.uriQueryInUnQuotedAttr(
    decodeURIComponent(getParentUrlParams.allowImproveExperience)
  );
}

// language
let parentLanguage;
if (getParentUrlParamsNoCache.language) {
  let filteredVal = xssFilters.uriQueryInUnQuotedAttr(
    getParentUrlParamsNoCache.language
  );
  if (includes(enabledLangsAndCountry, filteredVal)) {
    // defer to language sent by the consuming app
    // language has been enabled in app.config
    parentLanguage = filteredVal;
  }
}

//product
let parentProduct;
if (getParentUrlParamsNoCache.product) {
  parentProduct = xssFilters.uriQueryInUnQuotedAttr(
    decodeURIComponent(getParentUrlParamsNoCache.product)
  );
}

//release
let parentRelease;
if (getParentUrlParamsNoCache.release) {
  parentRelease = xssFilters.uriQueryInUnQuotedAttr(
    decodeURIComponent(getParentUrlParamsNoCache.release)
  );
}

// pageTitle
let parentPageTitle;
if (getParentUrlParamsNoCache.pageTitle) {
  parentPageTitle = xssFilters.uriQueryInUnQuotedAttr(
    decodeURIComponent(getParentUrlParamsNoCache.pageTitle)
  );
}
// dtVersion
let parentDtVersion;
if (getParentUrlParamsNoCache.dtVersion) {
  parentDtVersion = xssFilters.uriQueryInUnQuotedAttr(
    decodeURIComponent(getParentUrlParamsNoCache.dtVersion)
  );
}
// viewSource
let parentViewSource;
if (getParentUrlParamsNoCache.viewSource) {
  parentViewSource = xssFilters.uriQueryInUnQuotedAttr(
    decodeURIComponent(getParentUrlParamsNoCache.viewSource)
  );
}
// showFullPage
let parentShowFullPage;
if (getParentUrlParamsNoCache.showFullPage) {
  parentShowFullPage = xssFilters.uriQueryInUnQuotedAttr(
    decodeURIComponent(getParentUrlParamsNoCache.showFullPage)
  );
}

// Launch Darkly feature testing
let allowFeatureTestingParam;
if (getParentUrlParamsNoCache.allowFeatureTesting) {
  const filteredVal = xssFilters.uriQueryInUnQuotedAttr(
    decodeURIComponent(getParentUrlParamsNoCache.allowFeatureTesting)
  );

  allowFeatureTestingParam = filteredVal === 'true' ? true : false;
}

// Additional flag for testing w/ Launch Darkly
let allowFlagParam;
if (getParentUrlParamsNoCache.allowFlag) {
  const filteredVal = xssFilters.uriQueryInUnQuotedAttr(
    decodeURIComponent(getParentUrlParamsNoCache.allowFlag)
  );

  allowFlagParam = filteredVal === 'true' ? true : false;
}

// deep linking key passed from hosting site
let deepLinkTo;
if (getParentUrlParamsNoCache.deepLinkTo) {
  deepLinkTo = xssFilters.uriQueryInUnQuotedAttr(
    decodeURIComponent(getParentUrlParamsNoCache.deepLinkTo)
  );
}
// deep linking contact modality passed from hosting site
let modality;
if (getParentUrlParamsNoCache.modality) {
  modality = xssFilters.uriQueryInUnQuotedAttr(
    decodeURIComponent(getParentUrlParamsNoCache.modality)
  );
}

let integrationType;
if (getParentUrlParamsNoCache.integrationType) {
  integrationType = xssFilters.uriQueryInUnQuotedAttr(
    decodeURIComponent(getParentUrlParamsNoCache.integrationType)
  );
}

// value to determine if uhm is rendered in mobile device (<= 640 px)
let isMobileDevice;
if (getParentUrlParamsNoCache.isMobileDevice) {
  const filteredVal = xssFilters.uriQueryInUnQuotedAttr(
    decodeURIComponent(getParentUrlParamsNoCache.isMobileDevice)
  );
  isMobileDevice = filteredVal === 'true' ? true : false;
}

// Used for inline scheduler Cancel flow
let serviceappointid;
if (getParentUrlParamsNoCache.serviceappointid) {
  serviceappointid = xssFilters.uriQueryInUnQuotedAttr(
    decodeURIComponent(getParentUrlParamsNoCache.serviceappointid)
  );
}

// Used for inline scheduler Cancel flow
let Day;
if (getParentUrlParamsNoCache.Day) {
  Day = xssFilters.uriQueryInUnQuotedAttr(
    decodeURIComponent(getParentUrlParamsNoCache.Day)
  );
}

// Used for inline scheduler Cancel flow
let customerstarttime;
if (getParentUrlParamsNoCache.customerstarttime) {
  customerstarttime = xssFilters.uriQueryInUnQuotedAttr(
    decodeURIComponent(getParentUrlParamsNoCache.customerstarttime)
  );
}

// Used for inline scheduler Cancel flow
let customertimezone;
if (getParentUrlParamsNoCache.customertimezone) {
  customertimezone = xssFilters.uriQueryInUnQuotedAttr(
    decodeURIComponent(getParentUrlParamsNoCache.customertimezone)
  );
}

// Used for inline scheduler Cancel flow
let sacCancelLang;
if (getParentUrlParamsNoCache.sacCancelLang) {
  sacCancelLang = xssFilters.uriQueryInUnQuotedAttr(
    decodeURIComponent(getParentUrlParamsNoCache.sacCancelLang)
  );
}

// Used for inline scheduler Cancel flow
let agentType;
if (getParentUrlParamsNoCache.agentType) {
  agentType = xssFilters.uriQueryInUnQuotedAttr(
    decodeURIComponent(getParentUrlParamsNoCache.agentType)
  );
}

// Used for inline scheduler Cancel flow
let agentEmail;
if (getParentUrlParamsNoCache.agentEmail) {
  agentEmail = xssFilters.uriQueryInUnQuotedAttr(
    decodeURIComponent(getParentUrlParamsNoCache.agentEmail)
  );
}

// Used for inline scheduler Cancel flow
let sacProduct;
if (getParentUrlParamsNoCache.sacProduct) {
  sacProduct = xssFilters.uriQueryInUnQuotedAttr(
    decodeURIComponent(getParentUrlParamsNoCache.sacProduct)
  );
}

// Used for inline scheduler Cancel flow
let customerphone;
if (getParentUrlParamsNoCache.customerphone) {
  customerphone = xssFilters.uriQueryInUnQuotedAttr(
    decodeURIComponent(getParentUrlParamsNoCache.customerphone)
  );
}

// Used for inline scheduler Reschedule flow
let productCode;
if (getParentUrlParamsNoCache.productCode) {
  productCode = xssFilters.uriQueryInUnQuotedAttr(
    decodeURIComponent(getParentUrlParamsNoCache.productCode)
  );
}

// Used for inline scheduler Reschedule flow
let langCode;
if (getParentUrlParamsNoCache.langCode) {
  langCode = xssFilters.uriQueryInUnQuotedAttr(
    decodeURIComponent(getParentUrlParamsNoCache.langCode)
  );
}

// Used for inline scheduler Reschedule flow
let topic;
if (getParentUrlParamsNoCache.topic) {
  topic = xssFilters.uriQueryInUnQuotedAttr(
    decodeURIComponent(getParentUrlParamsNoCache.topic)
  );
}

// Used for inline scheduler Reschedule flow
let sacReschLang;
if (getParentUrlParamsNoCache.sacReschLang) {
  sacReschLang = xssFilters.uriQueryInUnQuotedAttr(
    decodeURIComponent(getParentUrlParamsNoCache.sacReschLang)
  );
}

// Used for contextual popup
let showContextualPopUp;
if (getParentUrlParamsNoCache.showContextualPopUp) {
  showContextualPopUp = xssFilters.uriQueryInUnQuotedAttr(
    decodeURIComponent(getParentUrlParamsNoCache.showContextualPopUp)
  );
}

// Used for inline scheduler Reschedule flow
let EvntSubject;
if (getParentUrlParamsNoCache.EvntSubject) {
  EvntSubject = xssFilters.uriQueryInUnQuotedAttr(
    decodeURIComponent(getParentUrlParamsNoCache.EvntSubject)
  );
}

// containerId - Not used for config
/* let parentContainerId;
if (getParentUrlParamsNoCache.containerId) {
  parentContainerId = xssFilters.uriQueryInUnQuotedAttr(
    decodeURIComponent(getParentUrlParamsNoCache.containerId)
  );
}
 */
let siteQueryStrings = '';

if (integrationType === 'In-Product') {
  // For in-product experience use the uhm domain as the source URL as there is no domain set by the product team.
  siteQueryStrings += '?srcUrl=' + siteTierConfig.srcUrlForInProduct;
  setNeutrontHandler();
} else if (parentUrl && parentUrl.length) {
  siteQueryStrings += '?srcUrl=' + encodeURIComponent(parentUrl);
}

if (parentLanguage && parentLanguage.length) {
  siteQueryStrings += '&locale=' + encodeURIComponent(parentLanguage);
} else {
  siteQueryStrings += '&locale=en_US';
}

if (parentProduct && parentProduct.length) {
  siteQueryStrings += '&plc=' + encodeURIComponent(parentProduct);
}
if (integrationType === 'In-Product') {
  siteQueryStrings += '&destination=In-Product';
} else if (parentShowFullPage && parentShowFullPage.length) {
  siteQueryStrings += '&destination=akn';
}

if (parentPageTitle && parentPageTitle.length) {
  siteQueryStrings += '&pageTitle=' + encodeURIComponent(parentPageTitle);
}
if (parentDtVersion && parentDtVersion.length) {
  siteQueryStrings += '&dtVersion=' + encodeURIComponent(parentDtVersion);
}
if (parentViewSource && parentViewSource.length) {
  siteQueryStrings += '&viewSource=' + encodeURIComponent(parentViewSource);
}

if (isMobileDevice) {
  siteQueryStrings += '&isMobileDevice=' + encodeURIComponent(isMobileDevice);
}

if (parentRelease) {
  siteQueryStrings += '&version=' + encodeURIComponent(parentRelease);
}

/*
if (parentContainerId && parentContainerId.length) {
  siteQueryStrings += '&containerId=' + parentContainerId;
} */

let getSitesConfig = siteTierConfig.sitesConfigUrl + siteQueryStrings;

// Make a request for the remote site config
axios
  .get(getSitesConfig)
  .then(async (response) => {
    // stop everything is sites-config returns non-200
    if (response.status !== 200) {
      console.warn('Sites config response not 200', response);
      return;
    }
    let siteData = response?.data ?? {};
    // enable dynatrace RUM
    if (siteTierConfig.dynatraceRumScript) {
      loadScriptFromUrl(siteTierConfig.dynatraceRumScript);
    }
    isLocalStorageAvailable &&
      localStorage?.setItem('uaCountry', siteData?.results?.country);
    let mySiteData;

    // don't render UA on OAuth2 redirects
    const {redirectOnly} = parseQueryParams(queryParams, ['redirectOnly']);

    // Is site allowed to integrate UHM
    // if not then show a console warning -> for production
    // we are allowing all sites to embed the non-prod version of uhm -> for testing
    if (
      // siteTier !== 'prd' || // non prod tier
      window.self === window.top || // not showing in iframe
      (siteData && siteData.statusCode && siteData.statusCode === 200) // site listed in config json
    ) {
      let linkToData = {};
      if (deepLinkTo) {
        const data = await workerInstance.getApiData({
          url: siteTierConfig.linkToMappingUrl,
          options: {
            timeout: 10000,
          },
        });
        linkToData = data && data[deepLinkTo];
      }

      const authParams = parseQueryParams(queryParams, [
        'accessToken',
        'authType',
        'entitlement',
        'code',
        'state',
      ]);
      // allow entitlment specification for PATs
      const entitlement = authParams?.entitlement || '';
      const useForgePAT = authParams?.authType === 'PAT';

      const forgeSetUpOptions = {
        authParams,
        entitlement,
        tier: siteTier,
        product: parentProduct,
        isInProduct: integrationType === 'In-Product' && !useForgePAT,
        parentDomain,
        utmSource: siteData?.results?.utmSource,
      };
      const waitForMeMenu = shouldUseAuthFromHostPage({
        parentDomain,
        utmSource: siteData?.results?.utmSource,
      });

      try {
        if (!waitForMeMenu) {
          forgeSetupAsync(forgeSetUpOptions);
        }
      } catch (e) {
        console.error('[Auth] - error setting up Forge Auth', e);
      }

      if (
        siteData.statusCode !== 200 && // site is not in config file but... //siteTier !== 'prd' ||
        window.self === window.top //...allow non-prod sites and non-iframe to include uhm non-prod tiers.
      ) {
        let useFallback = siteConfigFallbacks(siteTier); // use fallback site config
        mySiteData = {
          showLangFallbackMsg: false, // Init with default as false
          ...useFallback,
          pageUrl: parentUrl,
          pageDomain: parentDomain,
          pageReferrer: parentReferrer,
          release: parentRelease,
          meMenuLoading: waitForMeMenu,
        };
      } else {
        // else use site config from matched file
        mySiteData = {
          showLangFallbackMsg: false, // Init with default as false
          ...siteData.results,
          ...(integrationType && {integrationType}),
          pageUrl: parentUrl,
          pageDomain: parentDomain,
          pageReferrer: parentReferrer,
          linkTo: linkToData || {},
          linkToModality: modality,
          release: parentRelease,
          serviceappointid,
          Day,
          customerstarttime,
          customertimezone,
          sacCancelLang,
          agentType,
          agentEmail,
          sacProduct,
          customerphone,
          productCode,
          langCode,
          topic,
          sacReschLang,
          EvntSubject,
          showContextualPopUp,
          meMenuLoading: waitForMeMenu,
        };
      }
      // set language
      // coming from config
      // mySiteData.language = parentLanguage;
      document
        .getElementsByTagName('html')[0]
        .setAttribute('lang', mySiteData.language.split('_')[0]);

      //adskGdprAnalytics
      mySiteData.allowAnalyticsLogging =
        allowAnalyticsLogging && allowAnalyticsLogging === 'true'
          ? true
          : false;
      //Checks if storage is accessible
      isSessionStorageAvailable &&
        sessionStorage.setItem(
          'adsk_uhm_user_analytics_flag',
          allowAnalyticsLogging
        );

      //adskGdprImproveExp
      mySiteData.allowImproveExperience =
        allowImproveExperience && allowImproveExperience === 'true'
          ? true
          : false;
      //Checks if storage is accessible
      isSessionStorageAvailable &&
        sessionStorage.setItem(
          'adsk_uhm_user_improve_experience_flag',
          allowImproveExperience
        );
      // let parent know about lang
      if (window.top !== window) {
        window.parent.postMessage(
          {type: 'UHM_LANG', lang: mySiteData.language},
          mySiteData.pageDomain
        );
      }
      // intialize iframe post messaging and call oxygen
      initUhmFrameListeners();

      // Clean up previous app state from localstorage
      // having too many localstorage items can crash the app, when attempting to write to the store
      if (isLocalStorageAvailable) {
        let cutoffCtr = 10; // how many items do we want to keep
        let sortablePersistElems = []; // track the sortable keys
        let currentAppVer = `v${process.env.REACT_APP_VERSION}`; // we want to delete older versions of the app that might be in the store

        // lets get the keys currently in the store
        const persistKeys = Object.keys(window.localStorage).filter((key) =>
          key.startsWith('persist:uhmRoot')
        );

        // clean up only if there are more than the threshold
        if (persistKeys.length > cutoffCtr) {
          persistKeys.forEach((key) => {
            let expireKeyForPersistRoot = key.replace('persist:uhmRoot', 'exp');
            // first, eliminate any old versions
            if (!key.includes(currentAppVer)) {
              localStorage.removeItem(key);
              localStorage.removeItem(expireKeyForPersistRoot);
            } else {
              // these are the current app version values
              // get the expiration timestamps
              let expirationVal = localStorage.getItem(expireKeyForPersistRoot);
              if (expirationVal == null) {
                // oops looks like the expiration key is missing, lets remove the localstotage persist item
                localStorage.removeItem(key);
              } else {
                // we have both  a persist root and an expiration for it
                // add items for sorting as persist root key and the expiration val.
                // We will figure out the "exp_" key prefix later after sorting
                sortablePersistElems.push([key, expirationVal]);
              }
            }
          });

          // lets sort the custom array
          // newest items at the top
          sortablePersistElems.sort(function (a, b) {
            return b[1] - a[1];
          });

          // get the oldest items after the cutoff
          let slicedSortablePersistElems =
            sortablePersistElems.slice(cutoffCtr);

          // remove these older items
          slicedSortablePersistElems.forEach((key) => {
            let expireKeyForPersistRoot = key[0].replace(
              'persist:uhmRoot',
              'exp'
            );
            localStorage.removeItem(key[0]);
            localStorage.removeItem(expireKeyForPersistRoot);
          });
        }
      }

      // load Tealium tag, async=true
      loadScriptFromUrl(siteTierConfig.tealiumScript, true, initAnalytics);

      if (!redirectOnly) {
        (async () => {
          //storage is not accessible when iframe is seen as third party in incognito mode by the browser
          let launchdarklyId =
            isLocalStorageAvailable && localStorage.getItem('launchDarklyId');

          if (!launchdarklyId) {
            launchdarklyId = uuidv1();
            //storage is not accessible when iframe is seen as third party in incognito mode by the browser
            isLocalStorageAvailable &&
              localStorage.setItem('launchDarklyId', launchdarklyId);
          }

          const analyticsId = store.getState().userData?.analyticsId;
          const userId = store.getState().userData?.userDetails?.userid;
          const userEntitlementsData =
            store.getState().userData?.userEntitlements;
          const email = store.getState().userData?.userDetails?.email;
          const userEntitlementLevel =
            getUserEntitlementLevel(userEntitlementsData);

          let responseCaseCount;

          // NOTE: userEntitlementLevel is not yet known if this is the first page load for the user
          // We don't want to await here either because it will stall the rendering of the FAB
          // TODO: Update LdProvier user when forgeGetUserEntitlements() returns
          if (userEntitlementLevel.includes(`Enterprise`)) {
            log.debug('[cases][index.js][getting cases]');
            responseCaseCount = await eteRouter({
              payload: {email},
              tier: siteTier,
              type: 'queryCaseCount',
            });
          }

          const userObject = buildLdUserObject({
            ...mySiteData,
            allowFeatureTestingParam,
            allowFlagParam,
            analyticsId,
            userId,
            isMobileDevice,
            userEntitlementsData,
            userCaseCount: responseCaseCount?.data?.caseCount?.totalSize,
          });

          const LdProvider = await asyncWithLDProvider({
            clientSideID: siteTierConfig.ldClientID,
            user: {
              key: '22222',
              secondary: launchdarklyId,
              stickyUserId: launchdarklyId,
              ...userObject,
            },
            options: {
              bootstrap: 'localStorage',
            },
          });

          ReactDOM.render(
            <ProviderWrapper>
              <LdProvider>
                <App queryParams={queryParams} siteData={mySiteData} />
              </LdProvider>
            </ProviderWrapper>,
            document.getElementById('uhm-root')
          );
        })();
      }
    } else {
      // site is not allowed to use UHM yet
      log.warn(
        'This site is not allowed to use UHM yet. Please contact the DPE DHX UHM team.'
      );
      //Let the parent know the init is done.
      if (window.top !== window) {
        window.parent.postMessage({type: 'UHM_BLOCKED'}, parentDomain);
      }
    }
  })
  .catch(function (error) {
    log.error('Unable to load UHM init scripts.', error);
    
    // Report HTTP 431 and timeout errors to host page for Dynatrace tracking
    try {
      const parentDomain = getParentUrlParams.uhmParentDomain || '*';
      const errorType = error.response?.status === 431 ? 'REQUEST_HEADER_TOO_LARGE' : 
                        error.code === 'ECONNABORTED' ? 'TIMEOUT' :
                        error.response?.status ? `HTTP_${error.response.status}` :
                        'INITIALIZATION_ERROR';
      
      const errorMessage = error.message || 'Failed to initialize UHM';
      
      const estimateHeaderSize = () => {
        const url = window.location.href || '';
        const cookies = document.cookie || '';
        const userAgent = navigator.userAgent || '';
        return url.length + cookies.length + userAgent.length + 500;
      };
      
      window.parent.postMessage({
        type: 'UHM_LOAD_ERROR',
        errorType: errorType,
        errorMessage: errorMessage,
        errorDetails: {
          status: error.response?.status,
          statusText: error.response?.statusText,
          requestHeaderSize: estimateHeaderSize(),
          errorCode: error.code,
          url: window.location.href || 'unknown'
        }
      }, parentDomain);
    } catch (postError) {
      log.error('Failed to report error to host page:', postError);
    }
  });
